import React from "react";
import { TemplateProps, createTemplateUtils } from "../BaseTemplate";
import BasicInfoSection from "../../offer-wizard/BasicInfoSection";
import PublicPlanSummary from "../PublicPlanSummary";
import PublicFinancialSummary from "../PublicFinancialSummary";
import { analyticsService } from "../../../services/analyticsService";
import { getFormattedOfferValidityDate } from "../utils/offerUtils";

const MinimalTemplate: React.FC<TemplateProps> = ({
  offer,
  brandKit,
  termsAndConditions,
  editMode = false,
  onColorChange,
  showColorPicker,
  setShowColorPicker,
  onHeroUpload,
  onHeroRemove,
  uploadingHero = false,
  onTermsToggle,
  onTermsContentChange,
}) => {
  const utils = createTemplateUtils(brandKit);

  const handleClick = (e: React.MouseEvent) => {
    if (!editMode && analyticsService.isSessionActive()) {
      const rect = e.currentTarget.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      analyticsService.trackClick(
        (e.target as HTMLElement).tagName.toLowerCase(),
        x,
        y,
        "offer-page"
      );
    }
  };

  return (
    <div
      className="font-sans"
      onClick={handleClick}
      style={
        {
          "--primary-color": utils.colors.primary || "#1F2937",
          "--secondary-color": utils.colors.secondary || "#6B7280",
          "--accent-color": utils.colors.accent || "#3B82F6",
        } as React.CSSProperties
      }
    >
      <div className="max-w-3xl mx-auto bg-white px-8 py-12">
        {/* Key Visual Section */}
        {(utils.keyVisualUrl || editMode) && (
          <div className="relative group mb-16">
            {utils.keyVisualUrl ? (
              <div className="relative">
                <img
                  src={utils.keyVisualUrl}
                  alt="Key Visual"
                  className="w-full h-48 object-cover"
                />
                {editMode && (
                  <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-black group-hover:bg-opacity-40 transition-all duration-200 flex items-center justify-center">
                    <div className="hidden group-hover:flex space-x-4">
                      <button
                        onClick={() => {
                          const input = document.createElement("input");
                          input.type = "file";
                          input.accept = "image/jpeg,image/png,image/webp";
                          input.onchange = (e) => {
                            const file = (e.target as HTMLInputElement)
                              .files?.[0];
                            if (file) onHeroUpload?.(file);
                          };
                          input.click();
                        }}
                        className="px-4 py-2 bg-white text-gray-900 font-medium hover:bg-gray-50 transition-colors"
                        disabled={uploadingHero}
                      >
                        {uploadingHero ? "Uploading..." : "Podmień"}
                      </button>
                      <button
                        onClick={onHeroRemove}
                        className="px-4 py-2 bg-red-600 text-white font-medium hover:bg-red-700 transition-colors"
                      >
                        Usuń
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ) : editMode ? (
              <div className="h-32 border border-gray-200 flex items-center justify-center">
                <button
                  onClick={() => {
                    const input = document.createElement("input");
                    input.type = "file";
                    input.accept = "image/jpeg,image/png,image/webp";
                    input.onchange = (e) => {
                      const file = (e.target as HTMLInputElement).files?.[0];
                      if (file) onHeroUpload?.(file);
                    };
                    input.click();
                  }}
                  className="px-6 py-3 text-gray-700 border border-gray-300 hover:bg-gray-50 transition-colors font-medium"
                  disabled={uploadingHero}
                >
                  {uploadingHero ? "Uploading..." : "Dodaj Key Visual"}
                </button>
              </div>
            ) : null}
          </div>
        )}

        {/* Minimal Header */}
        <header className="mb-16 text-center">
          <div className="mb-8">
            <h1
              className="text-4xl font-light tracking-tight mb-4"
              style={{ color: utils.colors.primary || "#1F2937" }}
            >
              {offer.title}
            </h1>
            <div className="w-16 h-px bg-gray-300 mx-auto mb-6"></div>
            <p className="text-gray-600 text-lg font-light">
              Dla {offer.clientName}
            </p>
          </div>

          <div className="flex justify-between items-center py-4 border-t border-b border-gray-200">
            <div className="text-sm text-gray-500">{offer.hotel.name}</div>
            <div className="text-sm text-gray-500">
              Oferta #{offer.id.slice(-8).toUpperCase()}
            </div>
            <div className="text-sm text-gray-500">
              {utils.formatDate(new Date().toISOString())}
            </div>
          </div>
        </header>

        {/* Content Sections with minimal styling */}
        <div className="space-y-16 mb-16">
          <section>
            <BasicInfoSection
              data={{
                title: offer.title,
                clientName: offer.clientName,
                clientEmail: offer.clientEmail,
                clientPhone: offer.clientPhone,
                description: offer.description || "",
                checkInDate: offer.checkInDate,
                checkOutDate: offer.checkOutDate,
                numberOfGuests: offer.numberOfGuests,
                numberOfRooms: offer.numberOfRooms,
                offerDays: offer.offerDays,
                currentDay: 1,
                netValue: offer.netValue,
              }}
              mode="public"
            />
          </section>
          <section>
            <PublicPlanSummary offer={offer} />
          </section>
          <section>
            {offer.financialSummary && (
              <PublicFinancialSummary
                financialSummary={offer.financialSummary}
              />
            )}
          </section>
        </div>

        {/* Terms and Conditions Section */}
        {termsAndConditions?.enabled ? (
          <div className="relative group mb-16 py-8 border-t border-gray-200">
            {editMode && (
              <div className="absolute top-2 right-0 opacity-0 group-hover:opacity-100 transition-opacity">
                <button
                  onClick={() => onTermsToggle?.(false)}
                  className="px-3 py-1 bg-red-600 text-white text-sm hover:bg-red-700 transition-colors"
                >
                  Usuń
                </button>
              </div>
            )}

            <h3 className="text-xl font-light mb-6 text-gray-900">
              Warunki oferty
            </h3>

            {editMode ? (
              <textarea
                value={termsAndConditions.content || ""}
                onChange={(e) => onTermsContentChange?.(e.target.value)}
                placeholder="Wprowadź warunki oferty..."
                className="w-full h-24 p-3 border border-gray-300 rounded-none resize-none focus:ring-1 focus:ring-gray-400 focus:border-transparent font-sans"
              />
            ) : (
              <div
                className="text-gray-700 leading-relaxed"
                dangerouslySetInnerHTML={{
                  __html:
                    termsAndConditions.content?.replace(/\n/g, "<br>") || "",
                }}
              />
            )}
          </div>
        ) : editMode ? (
          <div className="border border-dashed border-gray-300 p-6 text-center mb-16">
            <button
              onClick={() => onTermsToggle?.(true)}
              className="px-6 py-3 text-gray-700 border border-gray-300 hover:bg-gray-50 transition-colors font-medium"
            >
              Dodaj sekcję "Warunki oferty"
            </button>
          </div>
        ) : null}

        {/* Minimal Call to Action */}
        <div className="text-center mb-16">
          <h3 className="text-2xl font-light text-gray-900 mb-8">
            Oczekujemy na Państwa odpowiedź
          </h3>

          <div className="flex flex-col sm:flex-row justify-center gap-4 mb-12">
            <button
              className="px-8 py-3 text-white font-medium hover:opacity-90 transition-opacity"
              style={{ backgroundColor: utils.colors.accent || "#3B82F6" }}
            >
              Akceptuję ofertę
            </button>
            <button className="px-8 py-3 bg-white border border-gray-300 text-gray-700 font-medium hover:bg-gray-50 transition-colors">
              Potrzebuję zmian
            </button>
          </div>

          {/* Minimal Contact Information */}
          <div className="border-t border-gray-200 pt-8">
            <h4 className="font-medium text-gray-900 mb-4">
              {offer.hotel.name}
            </h4>

            {offer.hotel.address && (
              <p className="text-gray-600 mb-4">{offer.hotel.address}</p>
            )}

            <div className="flex flex-col sm:flex-row justify-center items-center gap-6 text-sm">
              {offer.hotel.phone && (
                <a
                  href={`tel:${offer.hotel.phone}`}
                  className="text-gray-600 hover:opacity-80"
                >
                  {offer.hotel.phone}
                </a>
              )}
              {offer.hotel.email && (
                <a
                  href={`mailto:${offer.hotel.email}`}
                  className="text-gray-600 hover:opacity-80"
                >
                  {offer.hotel.email}
                </a>
              )}
              {offer.hotel.website && (
                <a
                  href={offer.hotel.website}
                  className="text-gray-600 hover:opacity-80"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {offer.hotel.website}
                </a>
              )}
            </div>
          </div>
        </div>

        {/* Minimal Footer */}
        <footer className="text-center text-sm text-gray-500 border-t border-gray-200 pt-8">
          <p>Oferta ważna do {getFormattedOfferValidityDate(offer, utils)}</p>
        </footer>
      </div>
    </div>
  );
};

export default MinimalTemplate;
